import { useEffect, useMemo, useState } from "react";
import { Upload, X, Check, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fetchProducts, uploadImage, updateProduct } from "@/lib/adminApi";
import { CATEGORY_LABELS } from "@/lib/i18n";

const CATS = ["all", "jerseys_countries", "jerseys_clubs", "jerseys_neutral", "cleats", "shoes", "accessories"];

/**
 * Drag-and-drop bulk image uploader where the admin manually pairs each
 * picture with a product (no filename matching required).
 *
 * UX:
 *  - ONE shared filter row at top (category + search) → narrows the product list for all photos at once.
 *  - Each photo row: thumbnail + product dropdown (filtered by shared category/search) + status.
 */
export default function VisualBulkUpload({ onClose, onDone }) {
  const [allProducts, setAllProducts] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]); // [{ id, file, previewUrl, slug, status, error }]
  const [submitting, setSubmitting] = useState(false);

  // Load every product (cap 500) once so the picker is instant.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchProducts({ limit: 500, page: 1 });
        if (!cancelled) setAllProducts(r.items || []);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error("Failed to load products for visual upload:", err);
        toast.error("Impossible de charger les produits");
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke object URLs on unmount only
  useEffect(() => {
    return () => {
      items.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filtered product list shared across all rows
  const filteredProducts = useMemo(() => {
    let list = allProducts;
    if (catFilter !== "all") list = list.filter((p) => p.category === catFilter);
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((p) =>
        p.name_fr.toLowerCase().includes(q) || p.slug.toLowerCase().includes(q),
      );
    }
    return list.slice(0, 200); // cap for browser perf
  }, [allProducts, catFilter, search]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (items.length + files.length > 30) {
      toast.error("Maximum 30 photos par lot. Recommence avec moins.");
      e.target.value = "";
      return;
    }
    const next = files.map((f) => ({
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      previewUrl: URL.createObjectURL(f),
      slug: "",
      status: "idle",
      error: "",
    }));
    setItems((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const setSlugFor = (id, slug) =>
    setItems((arr) => arr.map((it) => (it.id === id ? { ...it, slug } : it)));

  const applySlugToAll = (slug) => {
    if (!slug) return;
    setItems((arr) => arr.map((it) => (it.status === "done" ? it : { ...it, slug })));
    toast.success("Produit appliqué à toutes les photos");
  };

  const removeItem = (id) =>
    setItems((arr) => {
      const target = arr.find((it) => it.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return arr.filter((it) => it.id !== id);
    });

  const submit = async () => {
    const pending = items.filter((it) => it.slug && it.status !== "done");
    if (pending.length === 0) {
      toast.error("Associe au moins une photo à un produit");
      return;
    }
    setSubmitting(true);

    // Process sequentially so the running image counter per product stays consistent.
    const slugCache = {}; // slug -> latest images array
    for (const it of pending) {
      setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, status: "uploading", error: "" } : x)));
      try {
        let current = slugCache[it.slug];
        if (!current) {
          const prod = allProducts.find((p) => p.slug === it.slug);
          current = Array.isArray(prod?.images) ? [...prod.images] : [];
        }
        if (current.length >= 3) {
          setItems((arr) =>
            arr.map((x) =>
              x.id === it.id ? { ...x, status: "error", error: "Déjà 3 photos sur ce produit" } : x,
            ),
          );
          continue;
        }
        const up = await uploadImage(it.file);
        const newImages = [...current, up.url].slice(0, 3);
        await updateProduct(it.slug, { images: newImages });
        slugCache[it.slug] = newImages;
        setItems((arr) => arr.map((x) => (x.id === it.id ? { ...x, status: "done" } : x)));
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error("Visual upload row failed:", err);
        setItems((arr) =>
          arr.map((x) =>
            x.id === it.id ? { ...x, status: "error", error: err.response?.data?.detail || "Erreur" } : x,
          ),
        );
      }
    }

    setSubmitting(false);
    toast.success("Upload terminé");
    if (onDone) onDone();
  };

  const remaining = items.filter((it) => it.status !== "done").length;

  return (
    <div className="p-5 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.04]" data-testid="visual-upload-panel">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-yellow-300 mb-1">Upload visuel</p>
          <p className="text-[12px] text-white/70">
            <strong className="text-yellow-300">Étape 1 :</strong> Ajoute tes photos (n'importe quel nom).<br />
            <strong className="text-yellow-300">Étape 2 :</strong> Filtre la liste (catégorie + recherche) puis choisis un produit pour chaque photo.<br />
            <strong className="text-yellow-300">Étape 3 :</strong> Clique « Envoyer toutes les photos ».
            <br />
            📷 Max 3 photos par produit · Max 5 Mo par fichier · Formats JPG / PNG / WEBP
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="visual-upload-close"
          className="text-white/60 hover:text-white text-2xl leading-none px-2"
        >×</button>
      </div>

      {/* SHARED FILTER ROW — applies to all photo rows */}
      <div className="p-3 mb-4 rounded-xl bg-black/40 border border-yellow-500/20 flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/80">Filtrer la liste</span>
        <select
          value={catFilter}
          onChange={(e) => setCatFilter(e.target.value)}
          data-testid="visual-upload-cat-filter"
          className="text-xs px-2 py-1.5 rounded-md bg-black/60 border border-yellow-500/30 text-white"
        >
          {CATS.map((c) => (
            <option key={c} value={c}>{CATEGORY_LABELS[c].fr}</option>
          ))}
        </select>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-yellow-400/50" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tape un nom de produit (ex: Liverpool, Maillot Guinée…)"
            data-testid="visual-upload-search"
            className="w-full text-xs pl-7 pr-2 py-1.5 rounded-md bg-black/60 border border-yellow-500/30 text-white"
          />
        </div>
        <span className="text-[11px] text-yellow-300/70">
          {filteredProducts.length} produit(s) trouvé(s)
        </span>
      </div>

      {/* Pick files */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-yellow-500/40 text-yellow-300 cursor-pointer hover:border-yellow-400 ${submitting ? "opacity-50 pointer-events-none" : ""}`}
        >
          <Upload className="w-4 h-4" />
          Ajouter des photos
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={onPick}
            disabled={submitting}
            data-testid="visual-upload-input"
            className="hidden"
          />
        </label>
        {items.length > 0 && (
          <span className="text-xs text-yellow-300/80">
            {items.length} photo(s) — {remaining} en attente
          </span>
        )}
      </div>

      {/* Photo rows */}
      {items.length > 0 && (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1 mb-4" data-testid="visual-upload-list">
          {items.map((it, idx) => (
            <ItemRow
              key={it.id}
              index={idx}
              item={it}
              products={filteredProducts}
              onSlugChange={(s) => setSlugFor(it.id, s)}
              onApplyToAll={() => applySlugToAll(it.slug)}
              onRemove={() => removeItem(it.id)}
              showApplyToAll={items.length > 1}
            />
          ))}
        </div>
      )}

      {/* Submit */}
      <div className="flex gap-3">
        <Button
          onClick={submit}
          disabled={submitting || items.length === 0}
          data-testid="visual-upload-submit"
          className="rounded-full text-black font-bold tracking-widest disabled:opacity-40"
          style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
        >
          {submitting ? "Envoi en cours…" : "Envoyer toutes les photos"}
        </Button>
        {items.some((it) => it.status === "done") && (
          <Button
            onClick={() => {
              items.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
              setItems([]);
            }}
            data-testid="visual-upload-reset"
            className="rounded-full bg-transparent border border-white/20 text-white hover:bg-white/5"
          >
            Vider la liste
          </Button>
        )}
      </div>
    </div>
  );
}

function ItemRow({ index, item, products, onSlugChange, onApplyToAll, onRemove, showApplyToAll }) {
  const picked = products.find((p) => p.slug === item.slug)
    // also look up if filter hides it (already-picked product)
    || (item.slug ? { slug: item.slug, name_fr: item.slug, images: [] } : null);

  return (
    <div
      data-testid={`visual-upload-row-${item.id}`}
      className="flex items-center gap-3 p-2.5 rounded-xl border border-yellow-500/15 bg-black/30"
    >
      <span className="text-[10px] font-bold text-yellow-400/70 w-6 text-center flex-shrink-0">
        #{index + 1}
      </span>
      <div className="w-16 h-16 rounded-lg overflow-hidden bg-black flex items-center justify-center flex-shrink-0 border border-yellow-500/20">
        {item.previewUrl ? (
          <img src={item.previewUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon className="w-5 h-5 text-yellow-400/60" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <select
          value={item.slug}
          onChange={(e) => onSlugChange(e.target.value)}
          data-testid={`visual-upload-picker-${item.id}`}
          className="w-full text-xs px-2 py-1.5 rounded-md bg-black/40 border border-yellow-500/30 text-white"
        >
          <option value="">— Choisir le produit (utilise le filtre ci-dessus) —</option>
          {/* Keep current selection in the list even if filter hides it */}
          {item.slug && !products.find((p) => p.slug === item.slug) && (
            <option value={item.slug}>{item.slug} (sélection actuelle)</option>
          )}
          {products.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.name_fr} ({p.slug}){Array.isArray(p.images) && p.images.length >= 3 ? " · 3/3" : ""}
            </option>
          ))}
        </select>
        <div className="text-[11px] mt-1 truncate">
          {item.status === "uploading" && <span className="text-yellow-300 animate-pulse">⏳ Envoi…</span>}
          {item.status === "done" && (
            <span className="text-green-400 inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Photo associée à {picked?.name_fr || item.slug}
            </span>
          )}
          {item.status === "error" && <span className="text-red-400">❌ {item.error}</span>}
          {item.status === "idle" && picked && (
            <span className="text-white/60">
              → {picked.name_fr} · {picked.slug}
              {Array.isArray(picked.images) && picked.images.length >= 3 && (
                <span className="text-orange-300 ml-1">(déjà 3 photos)</span>
              )}
            </span>
          )}
          {item.status === "idle" && !picked && (
            <span className="text-white/40 italic">Pas encore associée</span>
          )}
        </div>
      </div>
      {showApplyToAll && item.slug && item.status !== "done" && (
        <button
          onClick={onApplyToAll}
          className="text-[10px] uppercase tracking-widest px-2.5 py-1.5 rounded-full border border-yellow-500/30 text-yellow-200 hover:border-yellow-400 flex-shrink-0"
          title="Appliquer ce produit à toutes les photos en attente"
          data-testid={`visual-upload-apply-all-${item.id}`}
        >
          ↓ Tout
        </button>
      )}
      <button
        onClick={onRemove}
        className="w-8 h-8 rounded-full border border-red-500/30 text-red-400 flex items-center justify-center hover:bg-red-500/10 flex-shrink-0"
        title="Retirer"
        data-testid={`visual-upload-remove-${item.id}`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
